<template>
  <v-container pt-4>

    <div v-if="payFeatureSwitchEnabled === null">
      Loading
    </div>

    <div v-else-if="payFeatureSwitchEnabled === true" class="my-3">
      <!-- New Setup Screen, replacing original free screen -->

      <!-- Opt-in section -->
      <TSOptIn
        v-model="optInMode"
        :msg="optInMessage"
        @change="setOptInMode"
      />
      <!-- Help Response Section -->
      <TSContactNum
        class="mt-3"
        :contact-num="contactNum"
        @contactNumberChanged="contactNumberChanged"
      />
      <!-- Phone Lookup Section -->
      <div class="headline blue--text">Phone number</div>
      <TSPhoneLookup
        class="mx-3"
        :area-code.sync="areaCode"
        :zip-code.sync="zipCode"
        @error="setValidationErr"
      />
      <!-- Monthly Fee -->
      <div class="headline blue--text">Monthly Fee</div>
      <v-checkbox
        v-model="consentToPayment"
        label="I understand my agency will be charged $20.00 per month starting on my next HawkSoft invoice."
      />
      <!-- Additional Blurb -->
      <div class="headline blue--text">Additional Required Information</div>
      <div class="mx-3">
        Telecom carriers require that all businesses sending text messages provide additional information to register their “brand” with The Campaign Registry (TCR). By proceeding to the next page, you are agreeing to provide this additional data to register with TCR.
        <a target="_blank" href="https://blog.hawksoft.com/10dlc-policies-introduction#policies">Learn More</a>
      </div>
    </div>

    <div v-else-if="payFeatureSwitchEnabled === false">
      <!-- OG Setup screen. Replaced by the above. Will be deleted eventually -->
      <OldTSOptIn
        v-model="optInMode"
        :msg="optInMessage"
        @change="setOptInMode"
      />
      <TSContactNum
        :contact-num="contactNum"
        @contactNumberChanged="contactNumberChanged"
      />
      <div class="headline mt-4 blue--text">Phone number</div>
      <OldTSPhoneLookup
        :area-code.sync="areaCode"
        :zip-code.sync="zipCode"
        @error="setValidationErr"
      />
    </div>

    <v-layout justify-end>
      <v-btn color="primary" :disabled="!formValid" @click="orderPhoneNumber">
        <span>Finish</span>
      </v-btn>
    </v-layout>

    <v-snackbar
      v-model="hasError"
      :timeout="3000"
      color="error"
    >
      {{ errorMessage }}
    </v-snackbar>

  </v-container>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import numAvailable from '@/utils/isPhoneAvailable.js';

import OldTSOptIn from './OldTSOptIn.vue';
import TSOptIn from './TSOptIn.vue';
import OldTSPhoneLookup from './OldTSPhoneLookup.vue';
import TSPhoneLookup from './TSPhoneLookup.vue';
import TSContactNum from './TSContactNum.vue';

export default {
  name: 'TextingSetup',
  components: {
    TSOptIn,
    OldTSOptIn,
    OldTSPhoneLookup,
    TSPhoneLookup,
    TSContactNum
  },
  data: () => ({
    errorMessage: '',
    hasError: false,
    optInMode: 0,
    optInStatusChanged: false,
    optInTimeStamp: '',
    userInitials: '',
    areaCode: '',
    zipCode: '',
    hasValidationErr: false,
    contactNum: '',
    contactNumValid: false,
    consentToPayment: false
  }),
  computed: {
    ...mapState([
      'agency',
      'featureSwitches'
    ]),
    optInMessage () {
      if (this.optInStatusChanged) return '';
      const timestamp = moment.utc(this.optInTimeStamp).local().format('MM/DD/YYYY hh:mm A');
      return `${this.userInitials} - ${timestamp}`;
    },
    formValid () {
      if (this.payFeatureSwitchEnabled && !this.consentToPayment) {
        return false;
      }

      const oneIsPopulated = (this.areaCode.length > 0 || this.zipCode.length > 0);
      const bothArePopulated = (this.areaCode.length > 0 && this.zipCode.length > 0);
      return this.contactNumValid &&
        (oneIsPopulated && !bothArePopulated) &&
        !this.hasValidationErr;
    },
    payFeatureSwitchEnabled () {
      if (this.featureSwitches === null) {
        return null;
      }
      if (this.featureSwitches.TextingProductStatusToggle == null) {
        return false;
      }
      return this.featureSwitches.TextingProductStatusToggle;
    }
  },
  watch: {
    optInMode (prevVal, newVal) {
      if (newVal === null) return; // `watch` is initialized earlier in lifecycle and will capture change to default `optInStatusChanged`
      if (prevVal !== newVal) this.optInStatusChanged = true;
    }
  },
  async created () {
    let p1 = this.loadTextingProfile();
    let p2 = this.getFeatureSwitches();
    await Promise.all([p1, p2]);
  },
  methods: {
    ...mapActions([
      'getFeatureSwitches'
    ]),
    async loadTextingProfile () {
      const response = await this.$api.getTextingProfile(this.agency.agencyId);
      if (response.data) {
        if (response.data.code !== 0) {
          if (response.data.code !== 2) {
            this.setError('Something went wrong. Unable to fetch texting information.');
          }
          return;
        }

        const agencyData = response.data.data[0];
        if ((agencyData || null) !== null) {
          this.optInMode = agencyData.optInMode;
          this.optInTimeStamp = agencyData.optInTimeStamp;
          this.userInitials = agencyData.optInUserInitials;

          if (agencyData.agencyContactNum !== null && agencyData.agencyContactNum.length === 12) {
            this.contactNum = agencyData.agencyContactNum.substr(2);
            this.contactNumValid = true;
          } else {
            this.contactNumValid = false;
          }
        }
      }
    },
    async orderPhoneNumber () {
      var searchBy = 0;
      if (this.zipCode.length > 0) {
        searchBy = 1;
      }

      const chkRs = await this.$api.checkNumAvailable(searchBy, this.areaCode, this.zipCode);
      if (numAvailable(chkRs)) {
        const errorMsg = `Phone numbers are unavailable for that ${searchBy === 0 ? 'area' : 'zip'} code, please try a different area code or zip code.`;
        this.setError(errorMsg);
        return;
      }

      const response = await this.$api.setOrderInfo(this.agency.name, this.optInMode, new Date().toISOString(), this.contactNum);
      if (response.data.code === 0) {
        const orderRs = await this.$api.orderNumber(searchBy, 1, this.areaCode, this.zipCode);

        if (orderRs.data.code !== 0) {
          this.setError('Error ordering number, please reach out to HawkSoft customer service to assistance.');
        } else {
          this.$router.push({ name: 'TextingSetupFinish' });
        }
      } else {
        if (response.data.message === 'ExternalPhone must be a valid 10-digit NANP phone number') {
          this.contactNumValid = false;
          this.setError(`The 'HELP' contact number does not appear to be a valid phone number`);
        } else {
          this.setError('Something went wrong when setting your agency information, please try again later.');
        }
      }
    },
    setValidationErr (bool) {
      this.hasValidationErr = bool;
    },
    setError (msg) {
      this.hasError = true;
      this.errorMessage = msg;
    },
    setOptInMode (v) {
      this.optInMode = v;
    },
    contactNumberChanged (num, valid) {
      this.contactNumValid = valid;
      if (valid) this.contactNum = num;
    }
  }
};
</script>

<style lang="scss" scoped>
  .v-label { font-size: 14px !important; }
</style>
